import React from 'react';
import Alert from '@mui/material/Alert';
import {
  AutocompleteInput,
  Edit,
  SimpleForm,
  ReferenceInput,
  SelectInput,
  NumberInput,
  FormDataConsumer,
  TopToolbar,
  ShowButton,
  Toolbar,
  minValue,
  required,
  useRecordContext,
  useTranslate,
} from "react-admin";
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { PriceInput, SaveConfirmButton } from '../../components';
import { FillinOrigin } from '../../models';
import moment from 'moment';
import { filterToQuery, formatAttendantLabel, formatNumber } from '../../utils';

const transform = (data) => {
  const { place, fuel, sale, ...rest } = data;
  return rest;
}

// Só serve para tirar o desconto: as opções são o desconto aplicado e "Sem desconto".
const DiscountInput = () => {
  const record = useRecordContext();
  if (!record?.discountId) { return null; }

  return (
    <ReferenceInput reference="discounts" source="discountId" filter={{ chainId: record.chainId, ids: [record.discountId] }}>
      <SelectInput
        optionText="identify"
        label="Desconto"
        emptyText="Sem desconto"
        sx={{ width: 250 }}
        fullWidth={false}
      />
    </ReferenceInput>
  );
};

const FillinEdit = () => {
  const translate = useTranslate();

  const EditActions = () => (
    <TopToolbar>
      <ShowButton />
    </TopToolbar>
  );

  const CustomToolbar = () => (
    <Toolbar>
      <FormDataConsumer>
        {({ formData }) => (
          <SaveConfirmButton
            formData={formData}
            validate={(data) => {
              if (data.pricePerLiter > 10 || data.pricePerLiter < 1) {
                return true;
              }
              return false
            }}
            contentMessage={
              <Alert
                variant="outlined"
                severity="warning"
              >
                O preço por litro é <strong>{formatNumber(formData.pricePerLiter, { style: 'currency', currency: 'BRL', minimumFractionDigits: 3, maximumFractionDigits: 3 })}</strong> <br/>
                Este valor parece incorreto. Tem certeza que deseja continuar?
              </Alert>
            }
          />
        )}
      </FormDataConsumer>
    </Toolbar>
  );

  return (
    <Edit title="Editar Abastecimneto" transform={transform} redirect={"show"} actions={<EditActions />}>
      <SimpleForm toolbar={<CustomToolbar />}>
        <ReferenceInput reference="chains" source="chainId">
          <SelectInput
            label="Rede"
            optionText="name"
            sx={{ width: 250 }}
            fullWidth={false}
            disabled
          />
        </ReferenceInput>
        <ReferenceInput reference="customers" source="customerId">
          <SelectInput
            label="Cliente"
            optionText="name"
            sx={{ width: 250 }}
            fullWidth={false}
            disabled
          />
        </ReferenceInput>
        <NumberInput source="amount" label="Quantidade em litros" validate={[minValue(0.01)]} sx={{ width: 200 }} fullWidth={false} />
        <PriceInput source="value" label="Valor pago" locales="pt-BR" validate={[minValue(0.01)]} sx={{ width: 180 }} fullWidth={false} />
        <PriceInput source="pricePerLiter" label="Preço por litro" precision={3} locales="pt-BR" validate={[minValue(0.001)]} sx={{ width: 180 }} fullWidth={false} />
        <PriceInput source="oldPricePerLiter" label="Preço por litro original" precision={3} locales="pt-BR" validate={[minValue(0.001)]} sx={{ width: 220 }} fullWidth={false} />
        <DiscountInput />
        <NumberInput source="hoseNumber" label="Número do bico" sx={{ width: 160 }} fullWidth={false} />
        <NumberInput source="encerrante" label="Encerrante" sx={{ width: 160 }} fullWidth={false} />
        <SelectInput
          source="origin"
          label="Origem"
          choices={FillinOrigin.fillinOriginChoices}
          sx={{ width: 200 }}
          fullWidth={false}
          isRequired
        />
        <ReferenceInput
          reference="fuels"
          source="fuelId"
          perPage={null}
          sort={{ field: 'name', order: 'ASC' }}
        >
          <SelectInput
            optionText="name"
            label="Combustível"
            sx={{ width: 220 }}
            fullWidth={false}
            isRequired
          />
        </ReferenceInput>
        <FormDataConsumer>
          {({ formData }) => (
            <ReferenceInput
              reference="stations"
              source="placeId"
              sort={{ field: 'name', order: 'ASC' }}
              filter={{ chainId: formData.chainId }}
              perPage={null}
            >
              <AutocompleteInput
                filterToQuery={filterToQuery}
                optionText="name"
                label="Posto"
                sx={{ width: 250 }}
                fullWidth={false}
                validate={[required()]}
                isRequired
                noOptionsText={translate('ra.input.autocomplete.no_options_text')}
              />
            </ReferenceInput>
          )}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => (
            <ReferenceInput
              emptyText="Não identificado"
              source="attendantId"
              reference="attendants"
              sort={{ field: 'name', order: 'ASC' }}
              filter={{ chainId: formData.chainId }}
              perPage={null}
            >
              <SelectInput
                optionText={record => formatAttendantLabel(record)}
                label="Frentista"
                sx={{ width: 250 }}
                fullWidth={false}
                isRequired
              />
            </ReferenceInput>
          )}
        </FormDataConsumer>
        <DateTimeInputUTC
          source="createdAt"
          label="Data do concentrador"
          locales="pt-BR"
          validate={required()}
          sx={{ width: 230 }}
          fullWidth={false}
        />
        <DateTimeInputUTC
          source="receivedAt"
          label="Data de recebimento"
          locales="pt-BR"
          validate={required()}
          sx={{ width: 230 }}
          fullWidth={false}
        />
      </SimpleForm>
    </Edit>
  );
};

export default FillinEdit;